import AddIcon from '@mui/icons-material/Add'
import {
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  SxProps,
  Typography,
  Tooltip,
  useTheme,
} from '@mui/material'
import { uniqueId } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'

import { CREATE_SCENARIO_ACTION_ROUTE_ID } from '~/features/scenarios/constants'
import useGetActions from '~/features/scenarios/hooks/useGetActions'
import { EActionKeys } from '~/features/scenarios/models'
import { pathForScenarioAction } from '~/features/scenarios/utils'
import rem from '~/utils/ui'

type TProps = {
  close: () => void
  sx?: SxProps
}

function ActionsList({ close, sx = {} }: TProps) {
  const { actionId: routeActionId } = useParams()
  const theme = useTheme()
  const navigate = useNavigate()
  const { data: actions, isLoading } = useGetActions()

  const handleNavigateToScenarioAction = (actionId: string) => {
    navigate(pathForScenarioAction(actionId))
    close()
  }

  const handleNavigateToCreateScenarioAction = () => {
    navigate(pathForScenarioAction(CREATE_SCENARIO_ACTION_ROUTE_ID))
    close()
  }

  return (
    <Stack height="40%" sx={{ ...sx }}>
      <Stack direction="row" alignItems="center">
        <Typography
          variant="h5"
          fontWeight={theme.typography.fontWeightBold}
          color={theme.palette.mainFont}
        >
          Manage Actions
        </Typography>
        <IconButton
          aria-label="close"
          size="large"
          onClick={handleNavigateToCreateScenarioAction}
        >
          <AddIcon />
        </IconButton>
      </Stack>
      <List sx={{ maxHeight: '100%', overflow: 'auto' }}>
        {isLoading ? (
          <Stack
            sx={{ height: rem(100) }}
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress color="inherit" />
          </Stack>
        ) : (
          actions
            ?.sort((a, b) =>
              a[EActionKeys.name] > b[EActionKeys.name] ? 1 : -1,
            )
            .map((action) => (
              <ListItemButton
                onClick={() =>
                  handleNavigateToScenarioAction(action[EActionKeys.id])
                }
                key={uniqueId()}
                sx={{
                  backgroundColor:
                    action[EActionKeys.id] === routeActionId
                      ? theme.palette.tertiary2
                      : 'transparent',
                }}
              >
                <Tooltip title={action[EActionKeys.name]} placement="top">
                  <ListItemText
                    primary={action[EActionKeys.name]}
                    sx={{
                      '& .MuiTypography-root': {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </Tooltip>
              </ListItemButton>
            ))
        )}
      </List>
    </Stack>
  )
}

export default ActionsList
